// THE WALL HOARD chest on the map. Tap → the card grows to full size and the lid-opening clip plays → when the lid is fully up the
// Hoard dock opens (HoardDock.jsx, same pattern as ON AIR). Minimizing runs the clip in reverse (a pre-reversed file — browsers
// can't play backwards reliably) with the heavy-door sound, then the card shrinks back to the closed chest.
import { useEffect, useRef, useState } from "react";
import { useHoardDock } from "./HoardDock";
import { CHEST_IMG, CHEST_OPEN_IMG, CHEST_VIDEO, CHEST_VIDEO_WEBM, CHEST_CLOSE_VIDEO, CHEST_CLOSE_VIDEO_WEBM, playChestSound } from "./chest";
export { CHEST_IMG, CHEST_OPEN_IMG, CHEST_VIDEO, CHEST_VIDEO_WEBM, CHEST_SOUND, CHEST_CLOSE_SOUND, troveMuted, playChestSound, playChestClose } from "./chest";

const LID_OPEN_AT = 4.9; // frame-diff analysis: the lid settles at ~4.8 s and the clip just holds after that — pop the dock right then

export const HoardChest = ({ night = false }) => {
  const h = useHoardDock();
  const open = h.open && !h.minimized;
  const openVid = useRef(null);
  const closeVid = useRef(null);
  const wrap = useRef(null);
  const [phase, setPhase] = useState("idle"); // idle | opening | opened | closing
  const setChestVisible = h.setChestVisible; // stable setter — depending on `h` re-ran this on every dock change (render loop)
  useEffect(() => {
    const el = wrap.current; if (!el || !("IntersectionObserver" in window)) return;
    const io = new IntersectionObserver(([e]) => setChestVisible(e.isIntersecting), { threshold: 0.4 });
    io.observe(el);
    return () => { io.disconnect(); setChestVisible(false); };
  }, [setChestVisible]);
  const safety = useRef(null);
  useEffect(() => () => clearTimeout(safety.current), []);
  const reset = () => { clearTimeout(safety.current); setPhase("idle"); const v = openVid.current; if (v) { v.pause(); v.currentTime = 0; v.load(); } };
  // Dock minimized/closed while the lid is up → play the lid shutting (reverse clip), then back to the closed chest.
  useEffect(() => {
    if (open || phase !== "opened") return;
    setPhase("closing");
    const v = closeVid.current;
    if (!v) { reset(); return; }
    v.currentTime = 0;
    const p = v.play?.();
    if (p?.catch) p.catch(reset);
    clearTimeout(safety.current);
    safety.current = setTimeout(reset, 6000); // never stuck "closing" if the clip stalls
  }, [open, phase]); // eslint-disable-line react-hooks/exhaustive-deps
  const opened = useRef(false);
  const finish = () => { if (opened.current) return; opened.current = true; clearTimeout(safety.current); setPhase("opened"); h.openDock(night); };
  const tap = () => {
    if (phase === "opening") { finish(); return; } // tap the big chest → straight to the Hoard
    if (phase === "closing") return;
    if (open) { h.minimize(); return; }
    playChestSound("home");
    opened.current = false;
    setPhase("opening");
    clearTimeout(safety.current);
    safety.current = setTimeout(finish, (LID_OPEN_AT + 1.6) * 1000); // the Hoard opens even if the clip stalls
    const p = openVid.current?.play?.();
    if (p?.catch) p.catch(finish);
  };
  const big = phase === "opening"; // the chest fills the whole map while it opens, then settles back into its card
  // While the Hoard is open or minimized (floating tile), the chest leaves the map; it returns when the Hoard is closed with ×.
  const hidden = h.open && phase === "idle";
  return (
    <div ref={wrap} className={`absolute bottom-2 left-2 transition-all duration-300 ease-out ${big ? "!inset-0 !w-auto z-[1200]" : "z-[500] w-[clamp(176px,48vw,330px)]"} ${hidden ? "hidden" : ""}`} data-testid="hoard-chest-wrap" data-phase={phase}>
      <button type="button" onClick={tap} aria-expanded={open} title={open ? "Close THE WALL HOARD" : "Open THE WALL HOARD"} aria-label={phase === "opening" ? "Opening THE WALL HOARD" : undefined}
        className={`group flex w-full items-center overflow-hidden rounded-xl bg-black ring-2 ring-[#F5D06F] shadow-2xl border-0 p-0 cursor-pointer ${big ? "h-full justify-center" : "hover:scale-[1.02] active:scale-[0.98] transition-transform duration-300"}`}
        data-testid="hoard-chest" data-insight="Map · Hoard chest">
        <span className={big ? "relative block h-full w-full" : "relative block w-[52%] shrink-0"} style={big ? undefined : { aspectRatio: "4 / 3" }}>
          <video ref={openVid} poster={CHEST_IMG} muted playsInline preload="metadata" disablePictureInPicture
            onTimeUpdate={(e) => { if (e.currentTarget.currentTime >= LID_OPEN_AT) finish(); }} onEnded={finish}
            className={`absolute inset-0 h-full w-full object-contain ${phase === "closing" ? "invisible" : ""}`} data-testid="hoard-chest-video">
            <source src={CHEST_VIDEO} type="video/mp4" />
            <source src={CHEST_VIDEO_WEBM} type="video/webm" />
          </video>
          <video ref={closeVid} poster={CHEST_OPEN_IMG} muted playsInline preload="metadata" disablePictureInPicture onEnded={reset}
            className={`absolute inset-0 h-full w-full object-contain ${phase === "closing" ? "" : "invisible"}`} data-testid="hoard-chest-reverse-video">
            <source src={CHEST_CLOSE_VIDEO} type="video/mp4" />
            <source src={CHEST_CLOSE_VIDEO_WEBM} type="video/webm" />
          </video>
        </span>
        {!big && (
          <span className="flex flex-1 self-stretch flex-col items-center justify-center px-[3%] py-1 text-center [container-type:inline-size]" data-testid="hoard-chest-label">
            <span className="block font-black uppercase tracking-[0.22em] text-[#F4E8CF]/70 text-[7cqw]">THE WALL</span>
            <span className="hoard-pirate block w-full leading-[0.95] text-[#F4E8CF] text-[21cqw]" data-testid="hoard-chest-title">Hoard of<br />Area Gems</span>
            <span className="mt-[3cqw] block font-bold uppercase tracking-[0.16em] text-white/70 text-[6.5cqw] whitespace-nowrap">{phase === "closing" ? "Closing…" : open ? "Tap to close" : "Tap to open"}</span>
          </span>
        )}
      </button>
    </div>
  );
};
